/* eslint-disable camelcase */
import { FC, useState } from 'react'
import { useNotify, useRecordContext, useTranslate } from 'react-admin'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import RefreshIcon from '@mui/icons-material/Refresh'
import ReplayIcon from '@mui/icons-material/Replay'
import { useNavigate } from 'react-router-dom'
import { getUrl, postJson } from '../../providers/data'
import { themeColor, xxllncColor } from '../../layout/theme/color'
import { aiTokens } from '../Workflow/aiGeneratorTokens'
import {
  DiagnosisOut,
  RetryWithDiagnosisOut,
  WorkflowExecutionOut,
} from '../../types/apiRequests'

const PreBlock: FC<{ data: unknown }> = ({ data }) => (
  <Box
    sx={{
      bgcolor: themeColor.slate05,
      borderRadius: '8px',
      border: `1px solid ${themeColor.slate10}`,
      p: 1.5,
      mt: 1,
    }}
  >
    <Typography
      component="pre"
      sx={{
        fontFamily: '"SF Mono", "Fira Code", "Consolas", monospace',
        fontSize: 11,
        lineHeight: 1.6,
        color: themeColor.slate80,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        m: 0,
      }}
    >
      {JSON.stringify(data, null, 2)}
    </Typography>
  </Box>
)

const AiDoctorPanel: FC = () => {
  const record = useRecordContext<WorkflowExecutionOut>()
  const translate = useTranslate()
  const notify = useNotify()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [retrying, setRetrying] = useState(false)
  const [diagnosis, setDiagnosis] = useState<DiagnosisOut | null>(null)
  const [error, setError] = useState<string | null>(null)

  if (!record || record.status !== 'failed') return null

  const fetchDiagnosis = async (forceRefresh: boolean) => {
    setLoading(true)
    setError(null)
    try {
      const result = await postJson<DiagnosisOut>(
        `${getUrl()}/workflow_execution/${record.id}/diagnose`,
        { force_refresh: forceRefresh },
      )
      setDiagnosis(result)
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e)
      setError(message)
    } finally {
      setLoading(false)
    }
  }

  const triggerRetry = async () => {
    if (!diagnosis) return
    setRetrying(true)
    try {
      const retried = await postJson<RetryWithDiagnosisOut>(
        `${getUrl()}/workflow_execution/${record.id}/retry_with_diagnosis`,
        { diagnosis_sid: diagnosis.diagnosis_sid },
      )
      notify(translate('workflow.doctor.retryStarted'), { type: 'info' })
      navigate(`/workflow_execution/${retried.execution_id}/show`)
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e)
      setError(message)
    } finally {
      setRetrying(false)
    }
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2.5,
        mb: 2,
        borderRadius: '12px',
        borderLeft: `4px solid ${aiTokens.accent}`,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <AutoFixHighIcon sx={{ color: aiTokens.accent, fontSize: 22 }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {translate('workflow.doctor.title')}
        </Typography>
        {diagnosis?.cached && (
          <Typography variant="caption" sx={{ color: themeColor.slate50, ml: 0.5 }}>
            {translate('workflow.doctor.cached')}
          </Typography>
        )}
      </Box>

      {!diagnosis && !error && (
        <>
          <Typography variant="body2" sx={{ color: themeColor.slate80, mb: 2 }}>
            {translate('workflow.doctor.intro')}
          </Typography>
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <AutoFixHighIcon />}
            disabled={loading}
            onClick={() => {
              void fetchDiagnosis(false)
            }}
            sx={{
              'bgcolor': aiTokens.accent,
              'textTransform': 'none',
              '&:hover': { bgcolor: aiTokens.accentHover },
            }}
          >
            {loading ? translate('workflow.doctor.analyzing') : translate('workflow.doctor.start')}
          </Button>
        </>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {diagnosis && (
        <Box>
          <Typography variant="caption" sx={{ color: themeColor.slate50, fontWeight: 600 }}>
            {translate('workflow.doctor.explanation')}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, mb: 2 }}>
            {diagnosis.explanation}
          </Typography>

          <Typography variant="caption" sx={{ color: themeColor.slate50, fontWeight: 600 }}>
            {translate('workflow.doctor.suggestedFix')}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, mb: 2 }}>
            {diagnosis.suggested_fix}
          </Typography>

          {diagnosis.suggested_inputs && (
            <>
              <Typography variant="caption" sx={{ color: themeColor.slate50, fontWeight: 600 }}>
                {translate('workflow.doctor.suggestedInputs')}
              </Typography>
              <PreBlock data={diagnosis.suggested_inputs} />
            </>
          )}

          {diagnosis.root_cause_node_id && (
            <Typography
              variant="caption"
              sx={{ display: 'block', mt: 2, color: themeColor.slate50 }}
            >
              {translate('workflow.doctor.rootCause')}: {diagnosis.root_cause_node_id}
            </Typography>
          )}

          <Box sx={{ display: 'flex', gap: 1, mt: 2.5 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<RefreshIcon />}
              disabled={loading}
              onClick={() => {
                void fetchDiagnosis(true)
              }}
              sx={{ textTransform: 'none', borderColor: aiTokens.accent, color: aiTokens.accent }}
            >
              {translate('workflow.doctor.refresh')}
            </Button>
            {diagnosis.retryable && (
              <Button
                size="small"
                variant="contained"
                startIcon={retrying ? <CircularProgress size={14} color="inherit" /> : <ReplayIcon />}
                disabled={retrying}
                onClick={() => {
                  void triggerRetry()
                }}
                sx={{
                  'textTransform': 'none',
                  'bgcolor': xxllncColor.green100,
                  '&:hover': { bgcolor: '#005A3A' },
                }}
              >
                {translate('workflow.doctor.retryWithFix')}
              </Button>
            )}
          </Box>
        </Box>
      )}
    </Paper>
  )
}

export default AiDoctorPanel
